import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CalendarDays, Search, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { APPT_TYPES, APPT_STATUS, REMINDER_OPTIONS } from "@/lib/appointments";
import { buildFullAddress, isAddressComplete } from "@/lib/calendar";

const EMPTY = {
  appt_date: "", appt_time: "", end_time: "", type: "Sopralluogo", status: "Programmato",
  location: "", address: "", notes: "", outcome: "", opportunity_id: "", supply_id: "",
  reminder_minutes: "", assignee_consultant_id: "", consultant_id: "",
};

export default function AppointmentDialog({
  open, onOpenChange, customerId, customerName, appointment, defaultDate,
  presetOpportunityId, presetType, onSaved,
}) {
  const { user } = useAuth();
  const editing = !!appointment;
  const [f, setF] = useState(EMPTY);
  const [reminderMode, setReminderMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [opps, setOpps] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const hardDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/appointments/${appointment.id}/hard`, { params: { confirm: true } });
      const g = data?.google === "deleted" ? " · evento Google Calendar eliminato" : data?.google === "not_found" ? " · nessun evento Google da eliminare" : "";
      toast.success(`Appuntamento eliminato definitivamente${g}`);
      setConfirmDelete(false);
      onSaved?.(null);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setDeleting(false); }
  };
  const [crmUsers, setCrmUsers] = useState([]);
  const canAssignUser = user?.role === "admin" || user?.role === "backoffice";
  const [busySlots, setBusySlots] = useState(null);
  const [conflictMsg, setConflictMsg] = useState("");
  const [addrComplete, setAddrComplete] = useState(true);

  const cid = customerId || picked?.id;

  useEffect(() => {
    if (!open) return;
    setError("");
    setPicked(customerId ? { id: customerId, display_name: customerName } : (appointment ? { id: appointment.customer_id, display_name: appointment.customer_name } : null));
    setTerm(""); setResults([]);
    if (appointment) {
      const rm = appointment.reminder_minutes;
      const preset = ["30", "120", "1440"].includes(String(rm)) ? String(rm) : (rm != null ? "custom" : "");
      setReminderMode(preset);
      setF({
        appt_date: appointment.appt_date || "", appt_time: appointment.appt_time || "", end_time: appointment.end_time || "",
        type: appointment.type || "Sopralluogo", status: appointment.status || "Programmato",
        location: appointment.location || "", address: appointment.address || "",
        notes: appointment.notes || "", outcome: appointment.outcome || "",
        opportunity_id: appointment.opportunity_id || "", supply_id: appointment.supply_id || "",
        reminder_minutes: rm != null ? String(rm) : "",
        assignee_consultant_id: appointment.assignee_consultant_id || "",
        consultant_id: appointment.consultant_id || "",
      });
    } else {
      setReminderMode("");
      setF({ ...EMPTY, appt_date: defaultDate || "", opportunity_id: presetOpportunityId || "", type: presetType || "Sopralluogo" });
    }
  }, [open, appointment, customerId, customerName, defaultDate, presetOpportunityId, presetType]);

  useEffect(() => {
    if (!cid) { setOpps([]); setSupplies([]); return; }
    api.get(`/customers/${cid}/opportunities`).then(({ data }) => setOpps(data)).catch(() => setOpps([]));
    api.get(`/customers/${cid}/supplies`).then(({ data }) => setSupplies(data)).catch(() => setSupplies([]));
  }, [cid]);

  useEffect(() => {
    if (customerId || !term) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/customers/search?q=${encodeURIComponent(term)}`).then(({ data }) => setResults(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [term, customerId]);

  useEffect(() => { if (open) api.get("/consultants").then(({ data }) => setConsultants(data)).catch(() => setConsultants([])); }, [open]);
  useEffect(() => { if (open && canAssignUser) api.get("/users/assignable").then(({ data }) => setCrmUsers(data)).catch(() => setCrmUsers([])); }, [open, canAssignUser]);

  // Propagazione automatica indirizzo dal cliente + indicatore indirizzo incompleto
  useEffect(() => {
    if (!cid) return;
    api.get(`/customers/${cid}`).then(({ data }) => {
      setAddrComplete(isAddressComplete(data));
      setF((s) => ({ ...s, address: s.address || buildFullAddress(data) }));
    }).catch(() => {});
  }, [cid]);

  // Disponibilità Google del consulente assegnatario per la giornata scelta
  useEffect(() => {
    if (!f.assignee_consultant_id || !f.appt_date) { setBusySlots(null); return; }
    api.get("/calendar/freebusy", { params: { consultant_id: f.assignee_consultant_id, date: f.appt_date } })
      .then(({ data }) => setBusySlots(data)).catch(() => setBusySlots(null));
  }, [f.assignee_consultant_id, f.appt_date]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onReminderMode = (v) => {
    setReminderMode(v === "none" ? "" : v);
    if (v === "custom") set("reminder_minutes", f.reminder_minutes || "60");
    else set("reminder_minutes", v === "none" ? "" : v);
  };

  const save = async (force = false) => {
    if (!cid) { setError("Seleziona un cliente"); return; }
    if (!f.appt_date) { setError("Indica la data dell'appuntamento"); return; }
    if (f.appt_time && f.end_time && f.end_time <= f.appt_time) { setError("L'ora di fine deve essere successiva all'ora di inizio"); return; }
    setError(""); setConflictMsg(""); setSaving(true);
    try {
      const payload = {
        customer_id: cid, appt_date: f.appt_date, appt_time: f.appt_time || null, end_time: f.end_time || null,
        type: f.type || null, status: f.status || "Programmato", location: f.location || null, address: f.address || null,
        notes: f.notes || null, outcome: f.outcome || null,
        opportunity_id: f.opportunity_id || null, supply_id: f.supply_id || null,
        reminder_minutes: f.reminder_minutes ? parseInt(f.reminder_minutes, 10) : null,
        assignee_consultant_id: f.assignee_consultant_id || null, force,
        ...(canAssignUser ? { consultant_id: f.consultant_id || null } : {}),
      };
      const { data } = editing ? await api.put(`/appointments/${appointment.id}`, payload) : await api.post(`/appointments`, payload);
      if (data?.google_sync_status === "failed") {
        toast.error(`${editing ? "Appuntamento aggiornato" : "Appuntamento creato"} nel CRM, ma NON sincronizzato su Google Calendar`, {
          description: data.google_sync_error || "Errore Google Calendar", duration: 12000, id: `gsync-${data.id}`,
        });
      } else if (data?.google_sync_status === "synced") {
        toast.success(`${editing ? "Appuntamento aggiornato" : "Appuntamento creato"} · evento Google Calendar ${editing ? "aggiornato" : "creato"}${data.assignee_consultant_name ? ` (${data.assignee_consultant_name})` : ""}`);
      } else {
        toast.success(editing ? "Appuntamento aggiornato" : "Appuntamento creato");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      const d = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 409 && String(d).includes("Google")) setConflictMsg(d);
      else setError(d);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><CalendarDays className="h-5 w-5 text-brand" /> {editing ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle>
          <DialogDescription>Collega l'appuntamento a un cliente e, se vuoi, a un'opportunità o una fornitura.</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="appointment-error">{error}</div>}

        <div className="space-y-4">
          {customerId ? (
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">Cliente: <strong>{customerName}</strong></div>
          ) : picked ? (
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <span>Cliente: <strong>{picked.display_name}</strong></span>
              <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>Cambia</Button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Cliente *</Label>
              <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9" placeholder="Cerca cliente (nome, telefono, azienda)…" value={term} onChange={(e) => setTerm(e.target.value)} data-testid="appt-customer-search" />
              </div>
              {results.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border">
                  {results.map((r) => (
                    <button key={r.id} onClick={() => { setPicked(r); setResults([]); setTerm(""); }} className="block w-full px-3 py-2 text-left text-sm hover:bg-brand/5" data-testid={`appt-pick-${r.id}`}>{r.display_name}{r.phone_primary ? ` · ${r.phone_primary}` : ""}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={f.appt_date} onChange={(e) => set("appt_date", e.target.value)} data-testid="appt-date" /></div>
            <div className="space-y-1.5"><Label>Inizio</Label><Input type="time" value={f.appt_time} onChange={(e) => set("appt_time", e.target.value)} data-testid="appt-time" /></div>
            <div className="space-y-1.5"><Label>Fine</Label><Input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} data-testid="appt-end-time" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Tipologia</Label>
              <Select value={f.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="appt-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{APPT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Stato</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="appt-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{APPT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {canAssignUser && (
              <div className="space-y-1.5 col-span-2"><Label>Consulente CRM (intestatario appuntamento)</Label>
                <Select value={f.consultant_id || "me"} onValueChange={(v) => set("consultant_id", v === "me" ? "" : v)}>
                  <SelectTrigger data-testid="appt-consultant-user"><SelectValue placeholder="Io" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="me">Io ({user?.full_name})</SelectItem>{crmUsers.filter((u) => u.id !== user?.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><Label>Appuntamento assegnato a</Label>
              <Select value={f.assignee_consultant_id || "none"} onValueChange={(v) => set("assignee_consultant_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="appt-assignee"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">Nessuno</SelectItem>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Disponibilità (Google)</Label>
              <div className="rounded-md border border-border px-3 py-2 text-xs text-slate-600" data-testid="appt-availability">
                {!f.assignee_consultant_id ? "—"
                  : !busySlots ? "…"
                  : !busySlots.connected ? "Calendario non collegato"
                  : busySlots.busy.length === 0 ? "Nessun impegno in giornata"
                  : "Occupato: " + busySlots.busy.map((b) => `${b.start}–${b.end}`).join(", ")}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Opportunità collegata</Label>
              <Select value={f.opportunity_id || "none"} onValueChange={(v) => set("opportunity_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="appt-opportunity"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">Nessuna</SelectItem>{opps.map((o) => <SelectItem key={o.id} value={o.id}>{o.product} · {o.stage}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Fornitura collegata</Label>
              <Select value={f.supply_id || "none"} onValueChange={(v) => set("supply_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="appt-supply"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">Nessuna</SelectItem>{supplies.map((s) => <SelectItem key={s.id} value={s.id}>{s.type}{s.pod_pdr ? ` · ${s.pod_pdr}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Luogo</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Es. Sede Mirandola / presso cliente" data-testid="appt-location" /></div>
            <div className="space-y-1.5"><Label>Promemoria interno</Label>
              <Select value={reminderMode || "none"} onValueChange={onReminderMode}>
                <SelectTrigger data-testid="appt-reminder"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{REMINDER_OPTIONS.map((r) => <SelectItem key={r.value || "none"} value={r.value || "none"}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {reminderMode === "custom" && (
            <div className="space-y-1.5"><Label>Minuti prima (personalizzato)</Label>
              <Input type="number" min="1" value={f.reminder_minutes} onChange={(e) => set("reminder_minutes", e.target.value)} data-testid="appt-reminder-custom" /></div>
          )}

          <div className="space-y-1.5"><Label>Indirizzo (sopralluogo)</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="appt-address" />
            {!addrComplete && <p className="mt-1 text-xs text-warning" data-testid="appt-address-incomplete">⚠ Indirizzo cliente incompleto: verifica via, civico e comune per il sopralluogo.</p>}
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          {editing && <div className="space-y-1.5"><Label>Esito</Label><Textarea value={f.outcome} onChange={(e) => set("outcome", e.target.value)} rows={2} data-testid="appt-outcome" /></div>}
        </div>

        {conflictMsg && (
          <div className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning" data-testid="appt-google-conflict">
            {conflictMsg}
            <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => save(true)} data-testid="appt-force-save">Conferma comunque</Button>
          </div>
        )}
        {editing && (
          <div className={`rounded-lg border px-3 py-2 text-xs ${appointment.google_sync_status === "synced" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : appointment.google_sync_status === "failed" ? "border-danger/30 bg-danger/10 text-danger" : "border-border bg-slate-50 text-slate-600"}`} data-testid="appt-google-status">
            <span className="font-semibold">Google Calendar:</span>{" "}
            {appointment.google_sync_status === "synced" && <>evento creato (ID {appointment.google_event_id}){appointment.google_synced_at ? ` · ${new Date(appointment.google_synced_at).toLocaleString("it-IT")}` : ""}</>}
            {appointment.google_sync_status === "failed" && <>NON sincronizzato — {appointment.google_sync_error || "errore Google"}</>}
            {appointment.google_sync_status === "cancelled" && <>evento rimosso dal calendario</>}
            {!appointment.google_sync_status && <>nessuna sincronizzazione (nessun consulente Google assegnato)</>}
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          <div>
            {editing && user?.role === "admin" && appointment.status === "Annullato" && (
              <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} disabled={deleting} data-testid="appt-hard-delete">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4" /> Elimina definitivamente</>}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="appt-cancel">Annulla</Button>
            {user?.can_manage_appointments
              ? <Button onClick={() => save()} disabled={saving} data-testid="appt-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
              : <span className="text-xs text-slate-500" data-testid="appt-readonly-note">Solo Direzione e Back Office possono fissare o modificare appuntamenti.</span>}
          </div>
        </DialogFooter>
        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent data-testid="appt-hard-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminazione definitiva</AlertDialogTitle>
              <AlertDialogDescription>Sei sicuro di voler eliminare definitivamente questo appuntamento? L'operazione non è reversibile.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="appt-hard-delete-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={hardDelete} className="bg-danger text-white hover:bg-danger/90" data-testid="appt-hard-delete-ok">Elimina definitivamente</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
